import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { localeMeta } from '@sohanscript/i18n/locale-meta';
import { type Locale, locales, setLocale } from '@sohanscript/i18n/runtime';
import { type ComponentProps, useEffect, useState } from 'react';
import { Button } from '../ui/button';

const DISMISSED_KEY = 'language-suggestion-dismissed';

// NOTE: the prompt waits for a while of active use, not just wall-clock time.
const SUGGESTION_DELAY_MS = 60_000;

const TIME_ZONE_LOCALE: Record<string, Locale> = {
	'Asia/Riyadh': 'ar',
	'Asia/Dubai': 'ar',
	'Asia/Qatar': 'ar',
	'Asia/Kuwait': 'ar',
	'Asia/Bahrain': 'ar',
	'Asia/Muscat': 'ar',
	'Africa/Cairo': 'ar',
	'Africa/Casablanca': 'ar',
	'Asia/Dhaka': 'bn',
	'Europe/Berlin': 'de',
	'Europe/Madrid': 'es',
	'Europe/Paris': 'fr',
	'Europe/Istanbul': 'tr',
	'Europe/Moscow': 'ru',
	'Europe/Warsaw': 'pl',
	'America/Sao_Paulo': 'pt',
	'Asia/Shanghai': 'zh',
};

type LanguageSuggestionProps = ComponentProps<'aside'>;

function localeFromBrowserPreferences(): Locale | undefined {
	const browserLanguages = navigator.languages.length
		? navigator.languages
		: [navigator.language];
	const supportedLocales = new Set<string>(locales);

	for (const language of browserLanguages) {
		const locale = language.split('-')[0]?.toLowerCase();
		if (locale && supportedLocales.has(locale)) return locale as Locale;
	}

	return undefined;
}

function suggestedLocale(): Locale | undefined {
	const browserLocale = localeFromBrowserPreferences();
	if (browserLocale && browserLocale !== 'en') return browserLocale;

	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	return TIME_ZONE_LOCALE[timeZone] ?? browserLocale;
}

function wasSuggestionDismissed(locale: Locale): boolean {
	try {
		return localStorage.getItem(DISMISSED_KEY) === locale;
	} catch {
		return false;
	}
}

function rememberDismissedSuggestion(locale: Locale): void {
	try {
		localStorage.setItem(DISMISSED_KEY, locale);
	} catch {
		return;
	}
}

export function LanguageSuggestion({
	className,
	...props
}: LanguageSuggestionProps) {
	const [suggestion, setSuggestion] = useState<Locale>();

	useEffect(() => {
		const nextLocale = suggestedLocale();
		if (!nextLocale || nextLocale === localeMeta().locale) return;
		if (wasSuggestionDismissed(nextLocale)) return;

		let remaining = SUGGESTION_DELAY_MS;
		let lastTick = Date.now();
		let timeoutId = 0;
		const clear = () => window.clearTimeout(timeoutId);
		const schedule = () => {
			clear();
			lastTick = Date.now();
			timeoutId = window.setTimeout(() => setSuggestion(nextLocale), remaining);
		};
		const handleVisibility = () => {
			if (document.hidden) {
				remaining = Math.max(0, remaining - (Date.now() - lastTick));
				clear();
			} else {
				schedule();
			}
		};
		document.addEventListener('visibilitychange', handleVisibility);
		schedule();
		return () => {
			document.removeEventListener('visibilitychange', handleVisibility);
			clear();
		};
	}, []);

	if (!suggestion) return null;

	const dismissSuggestion = () => {
		rememberDismissedSuggestion(suggestion);
		setSuggestion(undefined);
	};

	const switchLocale = () => {
		rememberDismissedSuggestion(suggestion);
		setLocale(suggestion);
	};

	return (
		<aside
			aria-label={m.lang_suggestion_label()}
			role="status"
			className={cn(
				'fixed animate-in slide-in-from-bottom inset-x-4 bottom-24 z-50 mx-auto flex w-[calc(100%-2rem)] max-w-sm flex-col gap-3 rounded-xl border border-primary/30 bg-popover p-4 text-popover-foreground shadow-(--shadow-popover) sm:inset-x-auto sm:inset-s-14 sm:bottom-5 sm:mx-0 sm:w-80',
				className,
			)}
			{...props}
		>
			<Button
				type="button"
				onClick={dismissSuggestion}
				aria-label={m.lang_suggestion_dismiss()}
				size="icon"
				variant="ghost"
				className="group hover:delay-100 absolute right-4 top-4"
			>
				<Iconify icon="lucide:x" className="size-4" />
			</Button>
			<div className="flex items-start gap-3">
				<Iconify
					icon="lucide:globe-2"
					className="mt-0.5 size-7 shrink-0 text-primary"
				/>
				<div className="min-w-0 flex-1">
					<p className="font-mono text-2xs text-primary uppercase tracking-[0.15em]">
						~/locale
					</p>
					<p className="mt-1 font-mono text-sm leading-relaxed text-foreground">
						{m.lang_suggestion_prompt({
							language: localeMeta(suggestion).languageName,
						})}
					</p>
				</div>
			</div>
			<Button type="button" onClick={switchLocale} className="w-full ">
				{m.lang_suggestion_switch()}
				<Iconify icon="lucide:arrow-right" className="size-4 rtl:rotate-180" />
			</Button>
		</aside>
	);
}
